import React from "react";
import { isFleetPerspectiveTarget } from "../utils/fleetTimelinePerspective.js";
import { getDeliveryHighlightKey, getLoadingHighlightKey, getLoadingOrderHighlightKey } from "../utils/resultsFleetData.js";

export const SEGMENT_REORDER_POINTER_HOLD_DELAY_MS = 240;
export const SEGMENT_REORDER_POINTER_MOVE_THRESHOLD_PX = 4;
export const SEGMENT_REORDER_SCOPE_LOADS = "loads";
export const SEGMENT_REORDER_SCOPE_DELIVERIES_WITH_SAME_PARENT_LOAD = "deliveries-with-same-parent-load";
export const SEGMENT_REORDER_SCOPE_DELIVERY_BLOCK = "delivery-block";

function normalizeSegmentReorderTimePriorityResetUpdates(updates = []) {
  if (!Array.isArray(updates) || updates.length === 0) {
    return [];
  }

  return updates.filter((item) => {
    if (!item || typeof item !== "object" || Array.isArray(item)) {
      return false;
    }

    const orderId = typeof item.orderId === "string" ? item.orderId.trim() : "";
    return Boolean(orderId && item.payload && typeof item.payload === "object" && !Array.isArray(item.payload));
  }).map((item) => ({
    ...item,
    orderId: item.orderId.trim(),
  }));
}

function createEmptyDropTarget() {
  return {
    assignmentId: "",
    deliveryBlockId: "",
    deliveryId: "",
    groupKey: "",
    manualMixBlockIndex: null,
    orderId: "",
    parentLoadingStepId: "",
    position: "before",
    scope: "",
    orderLabel: "",
    segmentId: "",
    segmentLabel: "",
    stepId: "",
    stepType: "",
    tripId: "",
  };
}

function normalizeSegmentReorderDispatchPeriod(value = "") {
  const normalizedValue = typeof value === "string" ? value.trim().toLowerCase() : "";
  return normalizedValue === "evening" || normalizedValue === "soir" ? "evening" : "day";
}

export function resolveSegmentReorderDispatchPeriodFromElement(element = null) {
  const rowElement = element?.closest?.('[data-fleet-timeline-row="true"]');
  return normalizeSegmentReorderDispatchPeriod(
    rowElement?.dataset?.dispatchPeriod || rowElement?.getAttribute?.("data-dispatch-period"),
  );
}

function coerceManualMixBlockIndex(value) {
  const parsed = Number(value);
  return Number.isInteger(parsed) ? parsed : null;
}

export function shouldEnforceSegmentReorderManualMixBlockBoundary(dragState = null) {
  void dragState;
  return false;
}

export function isSegmentReorderManualMixBlockMismatch(dragState = null, dropTarget = null) {
  void dragState;
  void dropTarget;
  return false;
}

function normalizeSegmentReorderSequenceId(value = "") {
  return typeof value === "string" ? value.trim() : "";
}

function compareSegmentReorderSequenceItems(leftSegment = null, rightSegment = null) {
  const leftSequence = Number(leftSegment?.globalSequence);
  const rightSequence = Number(rightSegment?.globalSequence);
  if (Number.isFinite(leftSequence) && Number.isFinite(rightSequence) && leftSequence !== rightSequence) {
    return leftSequence - rightSequence;
  }

  const leftStepId = normalizeSegmentReorderSequenceId(leftSegment?.segmentReorder?.stepId);
  const rightStepId = normalizeSegmentReorderSequenceId(rightSegment?.segmentReorder?.stepId);
  return leftStepId.localeCompare(rightStepId);
}

function findRowSegmentByReorderStepId(row = null, stepId = "") {
  const safeStepId = normalizeSegmentReorderSequenceId(stepId);
  if (!safeStepId || !Array.isArray(row?.segments)) {
    return null;
  }

  return row.segments.find((segment) => normalizeSegmentReorderSequenceId(segment?.segmentReorder?.stepId) === safeStepId) || null;
}

function normalizeSegmentReorderGroupKey(value = "") {
  return typeof value === "string" ? value.trim() : "";
}

function normalizeSegmentReorderIdList(values = []) {
  return (Array.isArray(values) ? values : [])
    .map((value) => normalizeSegmentReorderSequenceId(value))
    .filter(Boolean);
}

function addUniqueSegmentReorderId(target = [], seen = new Set(), value = "") {
  const normalizedValue = normalizeSegmentReorderSequenceId(value);
  if (!normalizedValue || seen.has(normalizedValue)) {
    return;
  }

  seen.add(normalizedValue);
  target.push(normalizedValue);
}

function buildSegmentReorderSourceGroupMemberIds(row = null, segment = null, sourceGroupKey = "", groupStepIds = []) {
  const reorderConfig = segment?.segmentReorder || null;
  const normalizedSourceGroupKey = normalizeSegmentReorderGroupKey(
    sourceGroupKey
    || segment?.sameSiteGroupKey
    || reorderConfig?.groupKey,
  );
  const sourceScope = normalizeSegmentReorderSequenceId(reorderConfig?.scope);
  const sourceStepType = normalizeSegmentReorderSequenceId(reorderConfig?.stepType);
  const stepIds = [];
  const orderIds = [];
  const seenStepIds = new Set();
  const seenOrderIds = new Set();

  const addSegment = (candidate = null) => {
    const candidateConfig = candidate?.segmentReorder || null;
    addUniqueSegmentReorderId(stepIds, seenStepIds, candidateConfig?.stepId);
    addUniqueSegmentReorderId(orderIds, seenOrderIds, candidateConfig?.orderId || candidate?.operationOrderId);
  };

  if (normalizedSourceGroupKey && Array.isArray(row?.segments)) {
    row.segments
      .slice()
      .sort(compareSegmentReorderSequenceItems)
      .forEach((candidate) => {
        const candidateConfig = candidate?.segmentReorder || null;
        const candidateGroupKey = normalizeSegmentReorderGroupKey(
          candidate?.sameSiteGroupKey
          || candidateConfig?.groupKey,
        );

        if (
          candidateGroupKey !== normalizedSourceGroupKey
          || (sourceScope && normalizeSegmentReorderSequenceId(candidateConfig?.scope) !== sourceScope)
          || (sourceStepType && normalizeSegmentReorderSequenceId(candidateConfig?.stepType) !== sourceStepType)
        ) {
          return;
        }

        addSegment(candidate);
      });
  }

  normalizeSegmentReorderIdList(groupStepIds).forEach((stepId) => {
    const matchedSegment = findRowSegmentByReorderStepId(row, stepId);
    if (matchedSegment) {
      addSegment(matchedSegment);
      return;
    }

    addUniqueSegmentReorderId(stepIds, seenStepIds, stepId);
  });

  return {
    orderIds,
    stepIds,
  };
}

function isSegmentReorderSameSourceGroupMemberDrop(dragState = null, dropTarget = null) {
  if (dragState?.sourceScope !== SEGMENT_REORDER_SCOPE_LOADS) {
    return false;
  }

  const sourceGroupKey = normalizeSegmentReorderGroupKey(dragState?.sourceGroupKey);
  const targetGroupKey = normalizeSegmentReorderGroupKey(dropTarget?.groupKey);
  if (sourceGroupKey && targetGroupKey && sourceGroupKey === targetGroupKey) {
    return true;
  }

  const sourceGroupStepIds = normalizeSegmentReorderIdList(
    Array.isArray(dragState?.sourceGroupReorderStepIds) && dragState.sourceGroupReorderStepIds.length > 0
      ? dragState.sourceGroupReorderStepIds
      : dragState?.sourceGroupStepIds,
  );
  const sourceGroupOrderIds = normalizeSegmentReorderIdList(dragState?.sourceGroupOrderIds);
  if (sourceGroupStepIds.length === 0 && sourceGroupOrderIds.length === 0) {
    return false;
  }

  const targetStepId = normalizeSegmentReorderSequenceId(dropTarget?.stepId);
  if (targetStepId && sourceGroupStepIds.includes(targetStepId)) {
    return true;
  }

  const targetOrderId = normalizeSegmentReorderSequenceId(dropTarget?.orderId);
  return Boolean(targetOrderId && sourceGroupOrderIds.includes(targetOrderId));
}

export function moveSegmentReorderSequenceItem(sequence = [], movedId = "", targetId = "", placement = "before") {
  const safeSequence = Array.isArray(sequence) ? sequence.map((item) => normalizeSegmentReorderSequenceId(item)).filter(Boolean) : [];
  const normalizedMovedId = normalizeSegmentReorderSequenceId(movedId);
  const normalizedTargetId = normalizeSegmentReorderSequenceId(targetId);
  if (!normalizedMovedId || !normalizedTargetId) {
    return safeSequence;
  }

  const reorderedSequence = safeSequence.filter((currentId) => currentId !== normalizedMovedId);
  const targetIndex = reorderedSequence.findIndex((currentId) => currentId === normalizedTargetId);
  if (targetIndex < 0) {
    return safeSequence;
  }

  // Séparer (fin de ligne) : le chargement passe après la cible.
  const insertIndex = targetIndex + (placement === "after" || placement === "separate" ? 1 : 0);
  reorderedSequence.splice(insertIndex, 0, normalizedMovedId);
  return reorderedSequence;
}

export function moveSegmentReorderSequenceItems(sequence = [], movedIds = [], targetId = "", placement = "before") {
  const safeSequence = Array.isArray(sequence) ? sequence.map((item) => normalizeSegmentReorderSequenceId(item)).filter(Boolean) : [];
  const normalizedMovedIds = (Array.isArray(movedIds) ? movedIds : [])
    .map((item) => normalizeSegmentReorderSequenceId(item))
    .filter(Boolean);
  const normalizedTargetId = normalizeSegmentReorderSequenceId(targetId);
  if (normalizedMovedIds.length === 0 || !normalizedTargetId) {
    return safeSequence;
  }

  const movedIdSet = new Set(normalizedMovedIds);
  const reorderedSequence = safeSequence.filter((currentId) => !movedIdSet.has(currentId));
  const targetIndex = reorderedSequence.findIndex((currentId) => currentId === normalizedTargetId);
  if (targetIndex < 0) {
    return safeSequence;
  }

  const insertIndex = targetIndex + (placement === "after" || placement === "separate" ? 1 : 0);
  for (let index = normalizedMovedIds.length - 1; index >= 0; index -= 1) {
    reorderedSequence.splice(insertIndex, 0, normalizedMovedIds[index]);
  }
  return reorderedSequence;
}

function areSegmentReorderSequencesEqual(leftSequence = [], rightSequence = []) {
  const safeLeftSequence = Array.isArray(leftSequence) ? leftSequence : [];
  const safeRightSequence = Array.isArray(rightSequence) ? rightSequence : [];
  if (safeLeftSequence.length !== safeRightSequence.length) {
    return false;
  }

  return safeLeftSequence.every((value, index) => value === safeRightSequence[index]);
}

// Un préchargement (Chargé PM) est déjà à bord : sa place parmi les chargements est celle de sa livraison.
function sortSegmentReorderScopeSegments(row = null, segments = []) {
  const rowSegments = Array.isArray(row?.segments) ? row.segments : [];
  const resolveOrderId = (item) => normalizeSegmentReorderSequenceId(item?.segmentReorder?.orderId || item?.operationOrderId);
  const isPreloadLoading = (item) => Boolean(item?.isPreloadedPreviousDay && item?.stopType === "loading");
  const preloadOrderIds = new Set(rowSegments.filter(isPreloadLoading).map(resolveOrderId).filter(Boolean));
  const preloadDeliverySequenceByOrderId = new Map();
  rowSegments.forEach((item) => {
    const orderId = resolveOrderId(item);
    const sequence = Number(item?.globalSequence);
    if (item?.stopType === "delivery" && preloadOrderIds.has(orderId) && Number.isFinite(sequence)) {
      preloadDeliverySequenceByOrderId.set(orderId, Math.min(sequence, preloadDeliverySequenceByOrderId.get(orderId) ?? sequence));
    }
  });
  const resolveSortSequence = (item) => (isPreloadLoading(item)
    ? preloadDeliverySequenceByOrderId.get(resolveOrderId(item))
    : undefined) ?? Number(item?.globalSequence);
  return segments
    .slice()
    .sort((left, right) => (resolveSortSequence(left) - resolveSortSequence(right)) || compareSegmentReorderSequenceItems(left, right));
}

export function buildSegmentReorderScopeSequence(row = null, segment = null) {
  const reorderConfig = segment?.segmentReorder || null;
  const scope = typeof reorderConfig?.scope === "string" ? reorderConfig.scope.trim() : "";
  if (!scope || !Array.isArray(row?.segments)) {
    return [];
  }

  const orderedSegments = sortSegmentReorderScopeSegments(
    row,
    row.segments.filter((item) => item?.segmentReorder?.scope === scope),
  );

  if (scope === SEGMENT_REORDER_SCOPE_LOADS) {
    const seenOrderIds = new Set();
    return orderedSegments.reduce((sequence, item) => {
      const orderId = normalizeSegmentReorderSequenceId(item?.segmentReorder?.orderId || item?.operationOrderId);
      if (!orderId || seenOrderIds.has(orderId)) {
        return sequence;
      }
      seenOrderIds.add(orderId);
      sequence.push(orderId);
      return sequence;
    }, []);
  }

  if (scope === SEGMENT_REORDER_SCOPE_DELIVERIES_WITH_SAME_PARENT_LOAD) {
    const parentLoadingStepId = normalizeSegmentReorderSequenceId(reorderConfig.parentLoadingStepId);
    const orderId = normalizeSegmentReorderSequenceId(reorderConfig.orderId);
    return orderedSegments.reduce((sequence, item) => {
      const config = item?.segmentReorder || null;
      if (
        normalizeSegmentReorderSequenceId(config?.parentLoadingStepId) !== parentLoadingStepId
        || normalizeSegmentReorderSequenceId(config?.orderId) !== orderId
      ) {
        return sequence;
      }

      const deliveryId = normalizeSegmentReorderSequenceId(config?.deliveryId || item?.operationDeliveryId);
      if (deliveryId) {
        sequence.push(deliveryId);
      }
      return sequence;
    }, []);
  }

  if (scope === SEGMENT_REORDER_SCOPE_DELIVERY_BLOCK) {
    const deliveryBlockId = normalizeSegmentReorderSequenceId(reorderConfig.deliveryBlockId);
    return orderedSegments.reduce((sequence, item) => {
      const config = item?.segmentReorder || null;
      if (normalizeSegmentReorderSequenceId(config?.deliveryBlockId) !== deliveryBlockId) {
        return sequence;
      }

      const deliveryId = normalizeSegmentReorderSequenceId(config?.deliveryId || item?.operationDeliveryId);
      if (deliveryId) {
        sequence.push(deliveryId);
      }
      return sequence;
    }, []);
  }

  return [];
}

function buildSegmentReorderScopeLoadingStepSequence(row = null, segment = null) {
  const reorderConfig = segment?.segmentReorder || null;
  const scope = typeof reorderConfig?.scope === "string" ? reorderConfig.scope.trim() : "";
  if (scope !== SEGMENT_REORDER_SCOPE_LOADS || !Array.isArray(row?.segments)) {
    return [];
  }

  const seenOrderIds = new Set();
  return sortSegmentReorderScopeSegments(row, row.segments.filter((item) => item?.segmentReorder?.scope === scope))
    .reduce((sequence, item) => {
      const orderId = normalizeSegmentReorderSequenceId(item?.segmentReorder?.orderId || item?.operationOrderId);
      const stepId = normalizeSegmentReorderSequenceId(item?.segmentReorder?.stepId);
      if (!orderId || !stepId || seenOrderIds.has(orderId)) {
        return sequence;
      }

      seenOrderIds.add(orderId);
      sequence.push({ orderId, stepId });
      return sequence;
    }, []);
}

export function resolveSegmentReorderMovedSequenceIds(row = null, segment = null, groupStepIds = []) {
  const reorderConfig = segment?.segmentReorder || null;
  const scope = typeof reorderConfig?.scope === "string" ? reorderConfig.scope.trim() : "";
  const safeGroupStepIds = (Array.isArray(groupStepIds) ? groupStepIds : [])
    .map((stepId) => normalizeSegmentReorderSequenceId(stepId))
    .filter(Boolean);
  const stepIds = safeGroupStepIds.length > 0
    ? safeGroupStepIds
    : [normalizeSegmentReorderSequenceId(reorderConfig?.stepId)].filter(Boolean);

  if (scope === SEGMENT_REORDER_SCOPE_LOADS) {
    const seenOrderIds = new Set();
    return stepIds.reduce((movedIds, stepId) => {
      const matchedSegment = findRowSegmentByReorderStepId(row, stepId);
      const orderId = normalizeSegmentReorderSequenceId(
        matchedSegment?.segmentReorder?.orderId || matchedSegment?.operationOrderId,
      );
      if (!orderId || seenOrderIds.has(orderId)) {
        return movedIds;
      }
      seenOrderIds.add(orderId);
      movedIds.push(orderId);
      return movedIds;
    }, []);
  }

  if (scope === SEGMENT_REORDER_SCOPE_DELIVERIES_WITH_SAME_PARENT_LOAD || scope === SEGMENT_REORDER_SCOPE_DELIVERY_BLOCK) {
    const seenDeliveryIds = new Set();
    return stepIds.reduce((movedIds, stepId) => {
      const matchedSegment = findRowSegmentByReorderStepId(row, stepId);
      const deliveryId = normalizeSegmentReorderSequenceId(
        matchedSegment?.segmentReorder?.deliveryId || matchedSegment?.operationDeliveryId,
      );
      if (!deliveryId || seenDeliveryIds.has(deliveryId)) {
        return movedIds;
      }
      seenDeliveryIds.add(deliveryId);
      movedIds.push(deliveryId);
      return movedIds;
    }, []);
  }

  return [];
}

export function isSegmentReorderNoOp(dragState = null, dropTarget = null) {
  if (isSegmentReorderSameSourceGroupMemberDrop(dragState, dropTarget)) {
    return true;
  }

  const scopeSequence = Array.isArray(dragState?.sourceScopeSequence) ? dragState.sourceScopeSequence : [];
  const movedIds = Array.isArray(dragState?.sourceMovedSequenceIds) ? dragState.sourceMovedSequenceIds : [];
  if (scopeSequence.length === 0 || movedIds.length === 0 || !dropTarget?.stepId) {
    return false;
  }

  const targetId = dragState.sourceScope === SEGMENT_REORDER_SCOPE_LOADS
    ? normalizeSegmentReorderSequenceId(dropTarget.orderId)
    : normalizeSegmentReorderSequenceId(dropTarget.deliveryId);
  if (!targetId || movedIds.includes(targetId)) {
    return true;
  }
  const joinedOrderIds = Array.isArray(dragState.sourceSegment?.joinedLoadOrderIds) ? dragState.sourceSegment.joinedLoadOrderIds : [];
  if (dropTarget.position === "join") {
    // Déjà chargés à la suite : seul un nouvel ordre de chargement (bloc glissé avant la cible) change quelque chose.
    if (!joinedOrderIds.includes(targetId)) {
      return false;
    }
    return areSegmentReorderSequencesEqual(scopeSequence, moveSegmentReorderSequenceItems(scopeSequence, movedIds, targetId, "before"));
  }
  // Au bord d'un autre chargement de son groupe « à la suite », seul l'ordre change. Ailleurs au bord, un
  // chargement fait à la suite d'un autre site (regroupé ou non) en est séparé, même sans changer l'ordre.
  const staysJoined = dropTarget.position !== "separate"
    && joinedOrderIds.includes(targetId)
    && movedIds.every((orderId) => joinedOrderIds.includes(orderId));
  const loadedTogetherOrderIds = [
    ...joinedOrderIds,
    ...(Array.isArray(dragState.sourceLoadingCycleOrderIds) ? dragState.sourceLoadingCycleOrderIds : []),
  ];
  if (!staysJoined && loadedTogetherOrderIds.some((orderId) => !movedIds.includes(orderId))) {
    return false;
  }

  const nextSequence = movedIds.length > 1
    ? moveSegmentReorderSequenceItems(scopeSequence, movedIds, targetId, dropTarget.position)
    : moveSegmentReorderSequenceItem(scopeSequence, movedIds[0], targetId, dropTarget.position);

  return areSegmentReorderSequencesEqual(scopeSequence, nextSequence);
}

// Commandes chargées dans le même cycle que le bloc glissé (chargements consécutifs avant une
// livraison), seulement si ce cycle passe par au moins deux sites; préchargements exclus.
export function buildSegmentReorderLoadingCycleOrderIds(row = null, movedOrderIds = []) {
  const orderIdOf = (item) => normalizeSegmentReorderSequenceId(item?.segmentReorder?.orderId || item?.operationOrderId);
  const steps = (Array.isArray(row?.segments) ? row.segments : [])
    .filter((item) => item?.stopType === "delivery" || (item?.stopType === "loading" && !item.isPreloadedPreviousDay))
    .sort((left, right) => Number(left.globalSequence) - Number(right.globalSequence));
  const index = steps.findIndex((item) => item.stopType === "loading" && movedOrderIds.includes(orderIdOf(item)));
  if (index < 0) {
    return [];
  }
  let start = index;
  let end = index;
  while (start > 0 && steps[start - 1].stopType === "loading") start -= 1;
  while (end + 1 < steps.length && steps[end + 1].stopType === "loading") end += 1;
  const cycle = steps.slice(start, end + 1);
  const sites = new Set(cycle.map((item) => item.operationAddress || item.operationLabel || ""));
  return sites.size > 1 ? [...new Set(cycle.map(orderIdOf).filter(Boolean))] : [];
}

export function buildDeliveryBlockParentLoadingStepIds(row = null, segment = null, groupStepIds = [], fallbackParentLoadingStepId = "") {
  const parentLoadingStepIds = [];
  const seenParentLoadingStepIds = new Set();
  const resolvedGroupStepIds = normalizeSegmentReorderIdList(groupStepIds);

  const addParentLoadingStepId = (value = "") => {
    addUniqueSegmentReorderId(parentLoadingStepIds, seenParentLoadingStepIds, value);
  };

  if (resolvedGroupStepIds.length > 0) {
    resolvedGroupStepIds.forEach((stepId) => {
      const matchedSegment = findRowSegmentByReorderStepId(row, stepId);
      addParentLoadingStepId(matchedSegment?.segmentReorder?.parentLoadingStepId);
    });
  }

  if (parentLoadingStepIds.length === 0) {
    addParentLoadingStepId(
      fallbackParentLoadingStepId || segment?.segmentReorder?.parentLoadingStepId,
    );
  }

  return parentLoadingStepIds;
}

function resolveDeliveryBlockLoadMovedSequenceIds(row = null, parentLoadingStepIds = []) {
  const orderIds = [];
  const seenOrderIds = new Set();

  normalizeSegmentReorderIdList(parentLoadingStepIds).forEach((loadingStepId) => {
    const matchedSegment = findRowSegmentByReorderStepId(row, loadingStepId);
    addUniqueSegmentReorderId(
      orderIds,
      seenOrderIds,
      matchedSegment?.segmentReorder?.orderId || matchedSegment?.operationOrderId,
    );
  });

  return orderIds;
}

function resolveDeliveryBlockLoadParentLoadingStepIds(dragState = null) {
  if (Array.isArray(dragState?.sourceLoadParentLoadingStepIds) && dragState.sourceLoadParentLoadingStepIds.length > 0) {
    return normalizeSegmentReorderIdList(dragState.sourceLoadParentLoadingStepIds);
  }

  return [normalizeSegmentReorderSequenceId(dragState?.sourceParentLoadingStepId)].filter(Boolean);
}

function buildDeliveryBlockLoadFallbackDragState(dragState = null) {
  if (dragState?.sourceScope !== SEGMENT_REORDER_SCOPE_DELIVERY_BLOCK) {
    return null;
  }

  const sourceLoadParentLoadingStepIds = resolveDeliveryBlockLoadParentLoadingStepIds(dragState);
  if (sourceLoadParentLoadingStepIds.length === 0) {
    return null;
  }

  const sourceLoadMovedSequenceIds = Array.isArray(dragState.sourceLoadMovedSequenceIds) && dragState.sourceLoadMovedSequenceIds.length > 0
    ? dragState.sourceLoadMovedSequenceIds
    : (Array.isArray(dragState.sourceGroupOrderIds) && dragState.sourceGroupOrderIds.length > 0
      ? normalizeSegmentReorderIdList(dragState.sourceGroupOrderIds)
      : [dragState.sourceOrderId].filter(Boolean));

  return {
    ...dragState,
    sourceDeliveryBlockId: "",
    sourceGroupKey: "",
    sourceGroupStepIds: sourceLoadParentLoadingStepIds,
    sourceHighlightStepKeys: Array.isArray(dragState.sourceLoadHighlightStepKeys)
      ? dragState.sourceLoadHighlightStepKeys
      : [],
    sourceLoadParentLoadingStepIds,
    sourceMovedSequenceIds: sourceLoadMovedSequenceIds,
    sourceParentLoadingStepId: "",
    sourceScope: SEGMENT_REORDER_SCOPE_LOADS,
    sourceScopeSequence: Array.isArray(dragState.sourceLoadScopeSequence)
      ? dragState.sourceLoadScopeSequence
      : [],
    sourceStepId: sourceLoadParentLoadingStepIds[0],
    sourceStepType: "loading",
  };
}

function normalizeDeliveryBlockLoadFallbackDropTarget(dragState = null, dropTarget = null) {
  const safeDropTarget = dropTarget && typeof dropTarget === "object"
    ? {
        ...createEmptyDropTarget(),
        ...dropTarget,
        position: dropTarget.position === "after" ? "after" : "before",
      }
    : createEmptyDropTarget();

  const sourceLoadParentLoadingStepIds = resolveDeliveryBlockLoadParentLoadingStepIds(dragState);
  if (
    dragState?.sourceScope !== SEGMENT_REORDER_SCOPE_DELIVERY_BLOCK
    || sourceLoadParentLoadingStepIds.length === 0
    || safeDropTarget.assignmentId !== dragState.sourceAssignmentId
  ) {
    return createEmptyDropTarget();
  }

  const targetLoadingStepId = safeDropTarget.stepType === "loading"
    ? safeDropTarget.stepId
    : safeDropTarget.stepType === "delivery"
      ? safeDropTarget.parentLoadingStepId
      : "";
  if (!targetLoadingStepId || sourceLoadParentLoadingStepIds.includes(targetLoadingStepId)) {
    return createEmptyDropTarget();
  }

  return {
    ...safeDropTarget,
    deliveryBlockId: "",
    deliveryId: "",
    parentLoadingStepId: "",
    scope: SEGMENT_REORDER_SCOPE_LOADS,
    stepId: targetLoadingStepId,
    stepType: "loading",
  };
}

function resolveEffectiveSegmentReorderContext(dragState = null, dropTarget = null) {
  // Un chargement lâché après sa propre dernière livraison, en fin de ligne : il passe après le dernier autre chargement.
  // Chargé à la suite d'autres, il en est séparé, quel que soit le dernier segment de la ligne.
  const movedIds = dragState?.sourceMovedSequenceIds || [];
  const lastOtherLoad = dragState?.sourceScope === SEGMENT_REORDER_SCOPE_LOADS && dropTarget?.isRowEnd
    ? [...(dragState.sourceScopeLoadingStepSequence || [])].reverse().find((item) => !movedIds.includes(item.orderId))
    : null;
  const separatesJoinedLoads = (dragState?.sourceSegment?.joinedLoadOrderIds || []).some((orderId) => !movedIds.includes(orderId));
  const rowEndDropTarget = lastOtherLoad
    ? normalizeSegmentReorderDropTarget(dragState, {
        ...dropTarget,
        deliveryBlockId: "",
        deliveryId: "",
        groupKey: "",
        orderId: lastOtherLoad.orderId,
        orderLabel: "",
        parentLoadingStepId: "",
        position: separatesJoinedLoads ? "separate" : "after",
        scope: SEGMENT_REORDER_SCOPE_LOADS,
        segmentId: "",
        segmentLabel: "",
        stepId: lastOtherLoad.stepId,
        stepType: "loading",
      })
    : createEmptyDropTarget();
  const rowEndContext = rowEndDropTarget.stepId && !isSegmentReorderNoOp(dragState, rowEndDropTarget)
    ? { dragState, dropTarget: rowEndDropTarget }
    : null;
  if (rowEndContext && separatesJoinedLoads) {
    return rowEndContext;
  }

  const normalizedDropTarget = normalizeSegmentReorderDropTarget(dragState, dropTarget);
  if (normalizedDropTarget.stepId && !isSegmentReorderNoOp(dragState, normalizedDropTarget)) {
    return {
      dragState,
      dropTarget: normalizedDropTarget,
    };
  }

  const fallbackDragState = buildDeliveryBlockLoadFallbackDragState(dragState);
  if (fallbackDragState) {
    const fallbackDropTarget = normalizeDeliveryBlockLoadFallbackDropTarget(dragState, dropTarget);
    const normalizedFallbackDropTarget = normalizeSegmentReorderDropTarget(fallbackDragState, fallbackDropTarget);
    if (
      normalizedFallbackDropTarget.stepId
      && !isSegmentReorderNoOp(fallbackDragState, normalizedFallbackDropTarget)
    ) {
      return {
        dragState: fallbackDragState,
        dropTarget: normalizedFallbackDropTarget,
      };
    }
  }

  return rowEndContext || {
    dragState,
    dropTarget: createEmptyDropTarget(),
  };
}

export function resolveEffectiveSegmentReorderDropTarget(dragState = null, dropTarget = null) {
  return resolveEffectiveSegmentReorderContext(dragState, dropTarget).dropTarget;
}

export function resolveSegmentReorderVisualDropTarget(rawDropTarget = null, effectiveDropTarget = null) {
  const safeEffectiveDropTarget = effectiveDropTarget?.stepId
    ? {
        ...createEmptyDropTarget(),
        ...effectiveDropTarget,
        position: normalizeSegmentReorderDropTargetPosition(effectiveDropTarget.position),
      }
    : createEmptyDropTarget();
  if (!safeEffectiveDropTarget.stepId) {
    return safeEffectiveDropTarget;
  }

  const safeRawDropTarget = rawDropTarget?.stepId
    ? {
        ...createEmptyDropTarget(),
        ...rawDropTarget,
        position: rawDropTarget.position === "after" ? "after" : "before",
      }
    : createEmptyDropTarget();
  if (!safeRawDropTarget.stepId) {
    return safeEffectiveDropTarget;
  }

  // Fin de ligne : la barre reste après le dernier segment, là où le chargement est lâché.
  if (safeRawDropTarget.isRowEnd && safeEffectiveDropTarget.stepType === "loading" && ["after", "separate"].includes(safeEffectiveDropTarget.position)) {
    return safeRawDropTarget;
  }

  if (
    safeEffectiveDropTarget.stepType === "loading"
    && safeRawDropTarget.stepType === "delivery"
    && safeRawDropTarget.parentLoadingStepId === safeEffectiveDropTarget.stepId
  ) {
    return {
      ...safeRawDropTarget,
      position: safeEffectiveDropTarget.position,
    };
  }

  return {
    ...safeEffectiveDropTarget,
    groupKey: safeEffectiveDropTarget.groupKey || safeRawDropTarget.groupKey || "",
    segmentId: safeEffectiveDropTarget.segmentId || safeRawDropTarget.segmentId || "",
  };
}

export function resolveSegmentReorderPlacement(bounds = null, clientX = 0, { allowJoin = false } = {}) {
  const safeBounds = bounds && Number.isFinite(bounds.left) && Number.isFinite(bounds.width)
    ? bounds
    : { left: 0, width: 0 };
  const width = Math.max(Number(safeBounds.width) || 0, 0);
  const offsetX = Number(clientX) - Number(safeBounds.left);
  // Chargements à la suite : le milieu d'un chargement charge avec lui; les bords réordonnent.
  if (allowJoin && offsetX > width / 4 && offsetX < (width * 3) / 4) {
    return "join";
  }
  return offsetX >= width / 2 ? "after" : "before";
}

export function resolveNearestSegmentReorderTargetFromBounds(rowSegmentBounds = [], clientX = 0) {
  const safeClientX = Number(clientX);
  if (!Array.isArray(rowSegmentBounds) || !Number.isFinite(safeClientX)) {
    return null;
  }

  const rowSegments = rowSegmentBounds
    .map((bounds, index) => {
      const left = Number(bounds?.left);
      const width = Math.max(Number(bounds?.width) || 0, 0);
      const right = Number.isFinite(Number(bounds?.right)) ? Number(bounds.right) : left + width;
      return { bounds: { left, right, width }, index };
    })
    .filter(({ bounds }) => Number.isFinite(bounds.left) && Number.isFinite(bounds.right))
    .sort((leftItem, rightItem) => leftItem.bounds.left - rightItem.bounds.left);

  if (rowSegments.length === 0) {
    return null;
  }

  for (const item of rowSegments) {
    if (safeClientX >= item.bounds.left && safeClientX <= item.bounds.right) {
      return {
        index: item.index,
        position: resolveSegmentReorderPlacement(item.bounds, safeClientX),
      };
    }

    if (safeClientX < item.bounds.left) {
      return {
        index: item.index,
        position: "before",
      };
    }
  }

  const lastItem = rowSegments[rowSegments.length - 1];
  return {
    index: lastItem.index,
    position: "after",
  };
}

function resolveNearestRowSegmentReorderTarget(target = null, clientX = 0) {
  if (!(target instanceof Element)) {
    return null;
  }

  const rowElement = target.closest('[data-fleet-timeline-row="true"]');
  if (!(rowElement instanceof HTMLElement)) {
    return null;
  }

  const safeClientX = Number(clientX);
  if (!Number.isFinite(safeClientX)) {
    return null;
  }

  const rowSegments = Array.from(rowElement.querySelectorAll('[data-fleet-segment-reorder="true"]'))
    .filter((element) => element instanceof HTMLElement)
    .map((element) => ({
      bounds: element.getBoundingClientRect(),
      element,
    }));
  const nearestTarget = resolveNearestSegmentReorderTargetFromBounds(
    rowSegments.map((item) => item.bounds),
    safeClientX,
  );
  if (!nearestTarget) {
    return null;
  }

  const matchedItem = rowSegments[nearestTarget.index];
  if (!matchedItem) {
    return null;
  }

  return {
    element: matchedItem.element,
    position: nearestTarget.position,
  };
}

function resolveSegmentReorderDropTargetFromClientPoint(clientX, clientY) {
  if (typeof document === "undefined") {
    return createEmptyDropTarget();
  }

  const target = document.elementFromPoint(clientX, clientY);
  if (isFleetPerspectiveTarget(target)) return createEmptyDropTarget();
  if (!(target instanceof Element)) {
    return createEmptyDropTarget();
  }

  let stepElement = target.closest('[data-fleet-segment-reorder="true"]');
  let resolvedPosition = "";
  if (!(stepElement instanceof HTMLElement)) {
    const rowTarget = resolveNearestRowSegmentReorderTarget(target, clientX);
    stepElement = rowTarget?.element || null;
    resolvedPosition = rowTarget?.position || "";
  }

  if (!(stepElement instanceof HTMLElement)) {
    return createEmptyDropTarget();
  }

  const position = resolvedPosition || resolveSegmentReorderPlacement(stepElement.getBoundingClientRect(), clientX, {
    allowJoin: stepElement.dataset.segmentReorderJoinable === "true",
  });
  // Après le dernier segment de la ligne : « à la fin de la journée ».
  const rowSegmentElements = Array.from(stepElement.closest('[data-fleet-timeline-row="true"]')?.querySelectorAll('[data-fleet-segment-reorder="true"]') || []);
  const isRowEnd = position === "after" && rowSegmentElements.every((element) => element.getBoundingClientRect().right <= stepElement.getBoundingClientRect().right);

  // Collapsed group with before/after bounds: use the appropriate bound
  const hasBeforeAfterBounds = Boolean(
    stepElement.dataset.segmentReorderBeforeStepId
    || stepElement.dataset.segmentReorderAfterStepId,
  );

  let effectiveStepId;
  let effectiveOrderId;
  let effectiveDeliveryId;
  let effectiveParentLoadingStepId;
  let effectiveManualMixBlockIndex;

  if (hasBeforeAfterBounds) {
    const boundPrefix = position === "after" ? "segmentReorderAfter" : "segmentReorderBefore";
    effectiveStepId = stepElement.dataset[boundPrefix + "StepId"] || "";
    effectiveOrderId = stepElement.dataset[boundPrefix + "OrderId"] || "";
    effectiveDeliveryId = stepElement.dataset[boundPrefix + "DeliveryId"] || "";
    effectiveParentLoadingStepId = stepElement.dataset[boundPrefix + "ParentLoadingStepId"] || "";
    effectiveManualMixBlockIndex = coerceManualMixBlockIndex(
      stepElement.dataset[boundPrefix + "ManualMixBlockIndex"],
    );
  } else {
    effectiveStepId = stepElement.dataset.segmentReorderStepId || "";
    effectiveOrderId = stepElement.dataset.segmentReorderOrderId || "";
    effectiveDeliveryId = stepElement.dataset.segmentReorderDeliveryId || "";
    effectiveParentLoadingStepId = stepElement.dataset.segmentReorderParentLoadingStepId || "";
    effectiveManualMixBlockIndex = coerceManualMixBlockIndex(
      stepElement.dataset.segmentReorderManualMixBlockIndex,
    );
  }

  return {
    assignmentId: stepElement.dataset.segmentReorderAssignmentId || "",
    deliveryId: effectiveDeliveryId,
    dispatchPeriod: resolveSegmentReorderDispatchPeriodFromElement(stepElement),
    groupKey: stepElement.dataset.segmentReorderGroupKey || "",
    deliveryBlockId: stepElement.dataset.segmentReorderDeliveryBlockId || "",
    isRowEnd,
    manualMixBlockIndex: effectiveManualMixBlockIndex,
    orderId: effectiveOrderId,
    parentLoadingStepId: effectiveParentLoadingStepId || stepElement.dataset.segmentReorderParentLoadingStepId || "",
    position,
    scope: stepElement.dataset.segmentReorderScope || "",
    orderLabel: stepElement.dataset.segmentReorderOrderLabel || "",
    segmentId: stepElement.dataset.segmentReorderSegmentId || "",
    segmentLabel: stepElement.dataset.segmentReorderSegmentLabel || "",
    stepId: effectiveStepId,
    stepType: stepElement.dataset.segmentReorderStepType || "",
    tripId: stepElement.dataset.segmentReorderTripId || "",
  };
}

function normalizeSegmentReorderDropTargetPosition(position = "") {
  return ["after", "join", "separate"].includes(position) ? position : "before";
}

function normalizeSegmentReorderDropTargetWithoutMixBlock(dragState = null, dropTarget = null) {
  let safeDropTarget = dropTarget && typeof dropTarget === "object"
    ? {
        ...createEmptyDropTarget(),
        ...dropTarget,
        position: normalizeSegmentReorderDropTargetPosition(dropTarget.position),
      }
    : createEmptyDropTarget();

  if (!dragState?.sourceStepId || !safeDropTarget.stepId) {
    return createEmptyDropTarget();
  }

  const sourceGroupKey = normalizeSegmentReorderGroupKey(dragState.sourceGroupKey);
  const targetGroupKey = normalizeSegmentReorderGroupKey(safeDropTarget.groupKey);
  if (dragState.sourceIsGroupedDrag && sourceGroupKey && targetGroupKey && sourceGroupKey === targetGroupKey) {
    return createEmptyDropTarget();
  }

  if (
    dragState.sourceScope === SEGMENT_REORDER_SCOPE_LOADS
    && safeDropTarget.stepType === "delivery"
    && safeDropTarget.parentLoadingStepId
  ) {
    safeDropTarget = {
      ...safeDropTarget,
      deliveryBlockId: "",
      deliveryId: "",
      parentLoadingStepId: "",
      scope: SEGMENT_REORDER_SCOPE_LOADS,
      stepId: safeDropTarget.parentLoadingStepId,
      stepType: "loading",
    };
  }

  if (
    safeDropTarget.assignmentId !== dragState.sourceAssignmentId
    || normalizeSegmentReorderDispatchPeriod(safeDropTarget.dispatchPeriod)
      !== normalizeSegmentReorderDispatchPeriod(dragState.sourceDispatchPeriod)
    || safeDropTarget.scope !== dragState.sourceScope
    || safeDropTarget.stepType !== dragState.sourceStepType
    || safeDropTarget.stepId === dragState.sourceStepId
  ) {
    return createEmptyDropTarget();
  }

  if (dragState.sourceScope === SEGMENT_REORDER_SCOPE_LOADS) {
    if (safeDropTarget.stepType !== "loading") {
      return createEmptyDropTarget();
    }

    const sourceGroupKey = typeof dragState.sourceGroupKey === "string"
      ? dragState.sourceGroupKey.trim()
      : "";
    const targetGroupKey = typeof safeDropTarget.groupKey === "string"
      ? safeDropTarget.groupKey.trim()
      : "";

    if (sourceGroupKey && targetGroupKey && sourceGroupKey === targetGroupKey) {
      return createEmptyDropTarget();
    }

    return safeDropTarget;
  }

  if (dragState.sourceScope === SEGMENT_REORDER_SCOPE_DELIVERIES_WITH_SAME_PARENT_LOAD) {
    if (
      safeDropTarget.stepType !== "delivery"
      || safeDropTarget.orderId !== dragState.sourceOrderId
      || safeDropTarget.parentLoadingStepId !== dragState.sourceParentLoadingStepId
    ) {
      return createEmptyDropTarget();
    }
    return safeDropTarget;
  }

  if (dragState.sourceScope === SEGMENT_REORDER_SCOPE_DELIVERY_BLOCK) {
    if (
      safeDropTarget.stepType !== "delivery"
      || !dragState.sourceDeliveryBlockId
      || safeDropTarget.deliveryBlockId !== dragState.sourceDeliveryBlockId
    ) {
      return createEmptyDropTarget();
    }
    return safeDropTarget;
  }

  return createEmptyDropTarget();
}

export function normalizeSegmentReorderDropTarget(dragState = null, dropTarget = null) {
  const normalizedDropTarget = normalizeSegmentReorderDropTargetWithoutMixBlock(dragState, dropTarget);
  if (!normalizedDropTarget.stepId) {
    return createEmptyDropTarget();
  }

  if (isSegmentReorderManualMixBlockMismatch(dragState, normalizedDropTarget)) {
    return createEmptyDropTarget();
  }

  return normalizedDropTarget;
}

export function buildSegmentReorderHighlightKeys({
  row = null,
  segment = null,
  groupStepIds = [],
} = {}) {
  const reorderConfig = segment?.segmentReorder || null;
  const scope = typeof reorderConfig?.scope === "string" ? reorderConfig.scope.trim() : "";
  if (!scope) {
    return [];
  }

  const keys = new Set();
  const safeGroupStepIds = (Array.isArray(groupStepIds) ? groupStepIds : [])
    .map((stepId) => normalizeSegmentReorderSequenceId(stepId))
    .filter(Boolean);
  const stepIds = safeGroupStepIds.length > 0
    ? safeGroupStepIds
    : [normalizeSegmentReorderSequenceId(reorderConfig?.stepId)].filter(Boolean);

  if (scope === SEGMENT_REORDER_SCOPE_DELIVERIES_WITH_SAME_PARENT_LOAD || scope === SEGMENT_REORDER_SCOPE_DELIVERY_BLOCK) {
    const parentLoadingStepId = normalizeSegmentReorderSequenceId(reorderConfig?.parentLoadingStepId || "");
    const parentLoadingKey = getLoadingHighlightKey(parentLoadingStepId);
    if (parentLoadingKey) {
      keys.add(parentLoadingKey);
    }

    stepIds.forEach((stepId) => {
      const matchedSegment = findRowSegmentByReorderStepId(row, stepId) || segment;
      const deliveryId = normalizeSegmentReorderSequenceId(
        matchedSegment?.segmentReorder?.deliveryId
        || matchedSegment?.operationDeliveryId
        || reorderConfig?.deliveryId
        || "",
      );
      const deliveryKey = getDeliveryHighlightKey(deliveryId);
      if (deliveryKey) {
        keys.add(deliveryKey);
      }
    });

    return [...keys];
  }

  if (scope === SEGMENT_REORDER_SCOPE_LOADS) {
    stepIds.forEach((loadingStepId) => {
      const matchedSegment = findRowSegmentByReorderStepId(row, loadingStepId);
      const loadingKey = getLoadingHighlightKey(loadingStepId);
      if (loadingKey) {
        keys.add(loadingKey);
      }

      const loadingOrderKey = getLoadingOrderHighlightKey(
        normalizeSegmentReorderSequenceId(
          matchedSegment?.segmentReorder?.orderId
          || matchedSegment?.operationOrderId
          || reorderConfig?.orderId
          || "",
        ),
      );
      if (loadingOrderKey) {
        keys.add(loadingOrderKey);
      }

      let deliveryId = normalizeSegmentReorderSequenceId(
        matchedSegment?.segmentReorder?.deliveryId
        || matchedSegment?.operationDeliveryId
        || "",
      );

      if (!deliveryId && Array.isArray(row?.segments)) {
        const deliverySegment = row.segments.find((candidate) => (
          (
            normalizeSegmentReorderSequenceId(candidate?.segmentReorder?.stepId) === loadingStepId
            || normalizeSegmentReorderSequenceId(candidate?.segmentReorder?.parentLoadingStepId) === loadingStepId
          )
          && normalizeSegmentReorderSequenceId(candidate?.segmentReorder?.deliveryId)
        ));
        deliveryId = normalizeSegmentReorderSequenceId(deliverySegment?.segmentReorder?.deliveryId || "");
      }

      const deliveryKey = getDeliveryHighlightKey(deliveryId);
      if (deliveryKey) {
        keys.add(deliveryKey);
      }
    });

    return [...keys];
  }

  return [];
}

function buildSegmentReorderTargetHighlightKeySet(dropTarget = null) {
  const keys = new Set();
  const targetStepType = normalizeSegmentReorderSequenceId(dropTarget?.stepType);
  const targetStepId = normalizeSegmentReorderSequenceId(dropTarget?.stepId);
  const targetOrderId = normalizeSegmentReorderSequenceId(dropTarget?.orderId);
  const targetDeliveryId = normalizeSegmentReorderSequenceId(dropTarget?.deliveryId);

  if (targetStepType === "loading") {
    const loadingKey = getLoadingHighlightKey(targetStepId);
    if (loadingKey) {
      keys.add(loadingKey);
    }

    const loadingOrderKey = getLoadingOrderHighlightKey(targetOrderId);
    if (loadingOrderKey) {
      keys.add(loadingOrderKey);
    }
  }

  const deliveryKey = getDeliveryHighlightKey(targetDeliveryId);
  if (deliveryKey) {
    keys.add(deliveryKey);
  }

  return keys;
}

function filterSegmentReorderSourceHighlightKeys(highlightKeys = [], dropTarget = null) {
  const targetHighlightKeys = buildSegmentReorderTargetHighlightKeySet(dropTarget);
  return (Array.isArray(highlightKeys) ? highlightKeys : [])
    .filter((stepKey) => typeof stepKey === "string" && stepKey)
    .filter((stepKey) => !targetHighlightKeys.has(stepKey));
}

function buildSegmentReorderPostDropLoadingHighlightKeys(dragState = null, dropTarget = null) {
  if (dragState?.sourceScope !== SEGMENT_REORDER_SCOPE_LOADS) {
    return [];
  }

  const scopeLoadingStepSequence = (Array.isArray(dragState?.sourceScopeLoadingStepSequence)
    ? dragState.sourceScopeLoadingStepSequence
    : [])
    .map((item) => ({
      orderId: normalizeSegmentReorderSequenceId(item?.orderId),
      stepId: normalizeSegmentReorderSequenceId(item?.stepId),
    }))
    .filter((item) => item.orderId && item.stepId);
  const scopeSequence = scopeLoadingStepSequence.length > 0
    ? scopeLoadingStepSequence.map((item) => item.orderId)
    : normalizeSegmentReorderIdList(dragState?.sourceScopeSequence);
  const scopeStepIds = scopeLoadingStepSequence.length > 0
    ? scopeLoadingStepSequence.map((item) => item.stepId)
    : [];
  const movedIds = normalizeSegmentReorderIdList(dragState?.sourceMovedSequenceIds);
  const targetId = normalizeSegmentReorderSequenceId(dropTarget?.orderId);
  if (scopeSequence.length === 0 || scopeStepIds.length !== scopeSequence.length || movedIds.length === 0 || !targetId) {
    return [];
  }

  const nextSequence = movedIds.length > 1
    ? moveSegmentReorderSequenceItems(scopeSequence, movedIds, targetId, dropTarget?.position)
    : moveSegmentReorderSequenceItem(scopeSequence, movedIds[0], targetId, dropTarget?.position);
  if (nextSequence.length !== scopeStepIds.length) {
    return [];
  }

  const keys = new Set();
  movedIds.forEach((orderId) => {
    const nextIndex = nextSequence.indexOf(orderId);
    const nextStepId = nextIndex >= 0 ? scopeStepIds[nextIndex] : "";
    const loadingKey = getLoadingHighlightKey(nextStepId);
    if (loadingKey) {
      keys.add(loadingKey);
    }
  });
  return [...keys];
}

function isSegmentReorderLoadingStepHighlightKey(stepKey = "") {
  return typeof stepKey === "string" && stepKey.startsWith("loading:");
}

function isSegmentReorderLoadingOrderHighlightKey(stepKey = "") {
  return typeof stepKey === "string" && stepKey.startsWith("loading-order:");
}

function isSegmentReorderDeliveryHighlightKey(stepKey = "") {
  return typeof stepKey === "string" && stepKey.startsWith("delivery:");
}

function buildSegmentReorderSourceLoadingOrderHighlightKeys(dragState = null) {
  if (dragState?.sourceScope !== SEGMENT_REORDER_SCOPE_LOADS) {
    return [];
  }

  const orderIds = normalizeSegmentReorderIdList(dragState?.sourceMovedSequenceIds);
  if (orderIds.length === 0) {
    const fallbackOrderId = normalizeSegmentReorderSequenceId(dragState?.sourceOrderId);
    if (fallbackOrderId) {
      orderIds.push(fallbackOrderId);
    }
  }

  return orderIds.map((orderId) => getLoadingOrderHighlightKey(orderId)).filter(Boolean);
}

function resolveSegmentReorderHighlightedStepKeys(dragState = null, dropTarget = null) {
  const sourceHighlightKeys = filterSegmentReorderSourceHighlightKeys(
    dragState?.sourceHighlightStepKeys,
    dropTarget,
  );
  const sourceLoadingOrderKeys = dragState?.sourceScope === SEGMENT_REORDER_SCOPE_LOADS
    ? Array.from(new Set([
      ...buildSegmentReorderSourceLoadingOrderHighlightKeys(dragState),
      ...sourceHighlightKeys.filter(isSegmentReorderLoadingOrderHighlightKey),
    ]))
    : [];
  if (sourceLoadingOrderKeys.length > 0) {
    return [
      ...sourceLoadingOrderKeys,
      ...sourceHighlightKeys.filter(isSegmentReorderDeliveryHighlightKey),
    ];
  }

  const postDropLoadingKeys = buildSegmentReorderPostDropLoadingHighlightKeys(dragState, dropTarget);
  if (postDropLoadingKeys.length === 0) {
    return sourceHighlightKeys;
  }

  return [
    ...postDropLoadingKeys,
    ...sourceHighlightKeys.filter((stepKey) => !isSegmentReorderLoadingStepHighlightKey(stepKey)),
  ];
}

export function buildSegmentReorderIntent(dragState = null, dropTarget = null) {
  const reorderContext = resolveEffectiveSegmentReorderContext(dragState, dropTarget);
  const effectiveDragState = reorderContext.dragState;
  const normalizedDropTarget = reorderContext.dropTarget;
  if (!effectiveDragState?.sourceStepId || !normalizedDropTarget.stepId) {
    return null;
  }

  const sourceGroupStepIds = Array.isArray(effectiveDragState.sourceGroupStepIds) && effectiveDragState.sourceGroupStepIds.length > 0
    ? effectiveDragState.sourceGroupStepIds
    : [effectiveDragState.sourceStepId];

  const draggedLabel = typeof effectiveDragState.sourcePreviewLabel === "string" && effectiveDragState.sourcePreviewLabel.trim()
    ? effectiveDragState.sourcePreviewLabel.trim()
    : buildSegmentReorderPreviewLabel(effectiveDragState.sourceSegment);
  const draggedOrderLabel = typeof effectiveDragState.sourceOrderLabel === "string" && effectiveDragState.sourceOrderLabel.trim()
    ? effectiveDragState.sourceOrderLabel.trim()
    : buildSegmentReorderOrderLabel(effectiveDragState.sourceSegment);
  const targetLabel = typeof normalizedDropTarget.segmentLabel === "string" && normalizedDropTarget.segmentLabel.trim()
    ? normalizedDropTarget.segmentLabel.trim()
    : "";
  const targetOrderLabel = typeof normalizedDropTarget.orderLabel === "string" && normalizedDropTarget.orderLabel.trim()
    ? normalizedDropTarget.orderLabel.trim()
    : "";
  const highlightedStepKeys = resolveSegmentReorderHighlightedStepKeys(
    effectiveDragState,
    normalizedDropTarget,
  );

  return {
    draggedSegmentId: effectiveDragState.sourceSegmentId || effectiveDragState.sourceStepId,
    draggedStepId: effectiveDragState.sourceStepId,
    draggedStepIds: sourceGroupStepIds,
    draggedLabel,
    draggedOrderLabel,
    dispatchPeriod: normalizeSegmentReorderDispatchPeriod(effectiveDragState.sourceDispatchPeriod),
    highlightedStepKeys,
    parentLoadingStepId: effectiveDragState.sourceParentLoadingStepId || null,
    deliveryBlockId: effectiveDragState.sourceDeliveryBlockId || null,
    placement: normalizedDropTarget.position,
    scope: effectiveDragState.sourceScope,
    sourceGroupKey: effectiveDragState.sourceGroupKey || null,
    targetSegmentId: normalizedDropTarget.segmentId || normalizedDropTarget.stepId,
    targetLabel,
    targetOrderLabel,
    targetStepId: normalizedDropTarget.stepId,
    timePriorityResetUpdates: normalizeSegmentReorderTimePriorityResetUpdates(effectiveDragState.sourceTimePriorityResetUpdates),
    truckId: effectiveDragState.sourceAssignmentId,
    truckLabel: typeof effectiveDragState.sourceTruckLabel === "string" ? effectiveDragState.sourceTruckLabel.trim() : "",
  };
}

export function buildSegmentReorderPreviewLabel(segment = null) {
  if (typeof segment?.operationLabel === "string" && segment.operationLabel.trim()) {
    return segment.operationLabel.trim();
  }
  if (typeof segment?.businessStep?.title === "string" && segment.businessStep.title.trim()) {
    return segment.businessStep.title.trim();
  }
  return typeof segment?.orderLabel === "string" && segment.orderLabel.trim()
    ? segment.orderLabel.trim()
    : "Segment";
}

export function buildSegmentReorderOrderLabel(segment = null) {
  if (typeof segment?.orderLabel === "string" && segment.orderLabel.trim()) {
    return segment.orderLabel.trim();
  }

  if (typeof segment?.operationOrderId === "string" && segment.operationOrderId.trim()) {
    return segment.operationOrderId.trim();
  }

  return "";
}

function buildSegmentReorderPreviewMeta(segment = null) {
  const parts = [
    typeof segment?.operationTypeLabel === "string" ? segment.operationTypeLabel.trim() : "",
    typeof segment?.quantityLabel === "string" ? segment.quantityLabel.trim() : "",
  ].filter(Boolean);
  return parts.join(" · ");
}

export default function useFleetSegmentReorderDrag({
  assignmentInteractionsRef = null,
  closeSegmentActionPopover = null,
  toggleTruckFilterDropdown = null,
} = {}) {
  const segmentReorderPointerGestureRef = React.useRef(null);
  const suppressSegmentReorderClickRef = React.useRef(false);
  const segmentReorderDragStateRef = React.useRef(null);
  const [segmentReorderDragState, setSegmentReorderDragState] = React.useState(null);
  const [segmentReorderInteractionState, setSegmentReorderInteractionState] = React.useState({
    phase: "idle",
    sourceStepId: "",
  });

  React.useEffect(() => {
    segmentReorderDragStateRef.current = segmentReorderDragState;
  }, [segmentReorderDragState]);

  const clearSegmentReorderClickSuppression = React.useCallback(() => {
    suppressSegmentReorderClickRef.current = false;
  }, []);

  const consumeSegmentReorderClickSuppression = React.useCallback(() => {
    if (!suppressSegmentReorderClickRef.current) {
      return false;
    }
    suppressSegmentReorderClickRef.current = false;
    return true;
  }, []);

  const clearSegmentReorderPointerGesture = React.useCallback(() => {
    const gesture = segmentReorderPointerGestureRef.current;
    if (!gesture) {
      return;
    }
    if (gesture.holdTimerId) {
      window.clearTimeout(gesture.holdTimerId);
    }
    gesture.cleanup?.();
    segmentReorderPointerGestureRef.current = null;
  }, []);

  const resetSegmentReorderDragState = React.useCallback(() => {
    clearSegmentReorderPointerGesture();
    setSegmentReorderDragState(null);
    setSegmentReorderInteractionState({ phase: "idle", sourceStepId: "" });
  }, [clearSegmentReorderPointerGesture]);

  const updateImmediateSegmentReorder = React.useCallback((pointerPosition = null) => {
    const clientX = Number.isFinite(Number(pointerPosition?.clientX)) ? Number(pointerPosition.clientX) : null;
    const clientY = Number.isFinite(Number(pointerPosition?.clientY)) ? Number(pointerPosition.clientY) : null;
    if (clientX == null || clientY == null) {
      return;
    }

    setSegmentReorderDragState((currentState) => {
      if (!currentState) {
        return currentState;
      }

      const rawDropTarget = resolveSegmentReorderDropTargetFromClientPoint(clientX, clientY);
      const dropTarget = resolveEffectiveSegmentReorderDropTarget(currentState, rawDropTarget);
      const visualDropTarget = resolveSegmentReorderVisualDropTarget(rawDropTarget, dropTarget);
      if (
        currentState.hoveredPlacement === dropTarget.position
        && currentState.hoveredGroupKey === (visualDropTarget.groupKey || "")
        && currentState.hoveredSegmentId === (visualDropTarget.segmentId || "")
        && currentState.hoveredStepId === (visualDropTarget.stepId || "")
        && currentState.pointerClientX === clientX
        && currentState.pointerClientY === clientY
      ) {
        return currentState;
      }

      return {
        ...currentState,
        hoveredGroupKey: visualDropTarget.groupKey || "",
        hoveredPlacement: dropTarget.position,
        hoveredSegmentId: visualDropTarget.segmentId || "",
        hoveredStepId: visualDropTarget.stepId || "",
        pointerClientX: clientX,
        pointerClientY: clientY,
      };
    });
  }, []);

  const startImmediateSegmentReorder = React.useCallback((row, segment, pointerPosition = null, previewOverrides = null) => {
    const reorderConfig = segment?.segmentReorder || null;
    if (
      !reorderConfig?.stepId
      || !reorderConfig?.stepType
      || !reorderConfig?.scope
      || !row?.assignmentId
    ) {
      return false;
    }

    assignmentInteractionsRef?.current?.onCloseReassignment?.();
    toggleTruckFilterDropdown?.(false);
    closeSegmentActionPopover?.();
    suppressSegmentReorderClickRef.current = true;

    const previewLabel = previewOverrides && typeof previewOverrides.label === "string" && previewOverrides.label.trim()
      ? previewOverrides.label.trim()
      : buildSegmentReorderPreviewLabel(segment);
    const previewMeta = previewOverrides && typeof previewOverrides.meta === "string" && previewOverrides.meta.trim()
      ? previewOverrides.meta.trim()
      : buildSegmentReorderPreviewMeta(segment);
    const previewSourceGroupKey = previewOverrides && typeof previewOverrides.sourceGroupKey === "string"
      ? previewOverrides.sourceGroupKey.trim()
      : "";

    const sourceGroupStepIds = Array.isArray(previewOverrides?.groupStepIds) && previewOverrides.groupStepIds.length > 0
      ? normalizeSegmentReorderIdList(previewOverrides.groupStepIds)
      : [];
    const sourceGroupKey = previewSourceGroupKey
      || normalizeSegmentReorderGroupKey(segment?.sameSiteGroupKey)
      || normalizeSegmentReorderGroupKey(reorderConfig.groupKey);
    const sourceGroupMemberIds = buildSegmentReorderSourceGroupMemberIds(
      row,
      segment,
      sourceGroupKey,
      sourceGroupStepIds,
    );
    const sourceScopeSequence = buildSegmentReorderScopeSequence(row, segment);
    const sourceMovedSequenceIds = resolveSegmentReorderMovedSequenceIds(row, segment, sourceGroupStepIds);
    const sourceHighlightStepKeys = buildSegmentReorderHighlightKeys({
      row,
      segment,
      groupStepIds: sourceGroupStepIds,
    });
    const sourceLoadParentLoadingStepIds = reorderConfig.scope === SEGMENT_REORDER_SCOPE_DELIVERY_BLOCK
      ? buildDeliveryBlockParentLoadingStepIds(
        row,
        segment,
        sourceGroupStepIds,
        reorderConfig.parentLoadingStepId,
      )
      : [];
    const sourceLoadStepId = sourceLoadParentLoadingStepIds[0]
      || (typeof reorderConfig.parentLoadingStepId === "string" ? reorderConfig.parentLoadingStepId : "");
    const sourceLoadSegment = sourceLoadStepId
      ? findRowSegmentByReorderStepId(row, sourceLoadStepId)
      : null;
    const sourceLoadScopeSequence = sourceLoadSegment
      ? buildSegmentReorderScopeSequence(row, sourceLoadSegment)
      : [];
    const sourceLoadMovedSequenceIds = sourceLoadParentLoadingStepIds.length > 0
      ? resolveDeliveryBlockLoadMovedSequenceIds(row, sourceLoadParentLoadingStepIds)
      : (typeof reorderConfig.orderId === "string" && reorderConfig.orderId.trim()
        ? [reorderConfig.orderId.trim()]
        : (sourceLoadSegment ? resolveSegmentReorderMovedSequenceIds(row, sourceLoadSegment, []) : []));
    const sourceLoadHighlightStepKeys = sourceLoadParentLoadingStepIds.length > 0
      ? Array.from(new Set(
        sourceLoadParentLoadingStepIds.flatMap((loadingStepId) => {
          const loadSegment = findRowSegmentByReorderStepId(row, loadingStepId);
          return loadSegment
            ? buildSegmentReorderHighlightKeys({ row, segment: loadSegment })
            : [];
        }),
      ))
      : (sourceLoadSegment
        ? Array.from(new Set([
          ...sourceHighlightStepKeys,
          ...buildSegmentReorderHighlightKeys({ row, segment: sourceLoadSegment }),
        ]))
        : sourceHighlightStepKeys);

    const nextState = {
      dragKind: "segment-reorder",
      hoveredGroupKey: "",
      hoveredPlacement: "before",
      hoveredSegmentId: "",
      hoveredStepId: "",
      pointerClientX: Number.isFinite(Number(pointerPosition?.clientX)) ? Number(pointerPosition.clientX) : 24,
      pointerClientY: Number.isFinite(Number(pointerPosition?.clientY)) ? Number(pointerPosition.clientY) : 24,
      sourceAssignmentId: row.assignmentId,
      sourceDeliveryBlockId: typeof reorderConfig.deliveryBlockId === "string" ? reorderConfig.deliveryBlockId : "",
      sourceDeliveryId: typeof reorderConfig.deliveryId === "string" ? reorderConfig.deliveryId : "",
      sourceDispatchPeriod: normalizeSegmentReorderDispatchPeriod(row?.dispatchPeriod),
      sourceGroupKey,
      sourceGroupOrderIds: sourceGroupMemberIds.orderIds,
      sourceGroupReorderStepIds: sourceGroupMemberIds.stepIds,
      sourceGroupStepIds,
      sourceHighlightStepKeys,
      sourceIsGroupedDrag: previewOverrides?.isGroupedDrag === true,
      sourceIsManualMix: Boolean(row?.assignment?.is_manual_mix || segment?.assignmentIsManualMix),
      sourceLoadHighlightStepKeys,
      sourceLoadMovedSequenceIds,
      sourceLoadParentLoadingStepIds,
      sourceLoadScopeSequence,
      sourceLoadingCycleOrderIds: reorderConfig.scope === SEGMENT_REORDER_SCOPE_LOADS
        ? buildSegmentReorderLoadingCycleOrderIds(row, sourceMovedSequenceIds)
        : [],
      sourceManualMixBlockIndex: coerceManualMixBlockIndex(reorderConfig?.manualMixBlockIndex),
      sourceMovedSequenceIds,
      sourceOrderId: typeof reorderConfig.orderId === "string" ? reorderConfig.orderId : "",
      sourceScopeSequence,
      sourceScopeLoadingStepSequence: buildSegmentReorderScopeLoadingStepSequence(row, segment),
      sourceOrderLabel: buildSegmentReorderOrderLabel(segment),
      sourceParentLoadingStepId: typeof reorderConfig.parentLoadingStepId === "string" ? reorderConfig.parentLoadingStepId : "",
      sourceScope: reorderConfig.scope,
      sourceSegment: segment,
      sourceSegmentId: typeof reorderConfig.segmentId === "string" && reorderConfig.segmentId
        ? reorderConfig.segmentId
        : (segment?.operationKey || reorderConfig.stepId),
      sourceStepId: reorderConfig.stepId,
      sourceStepType: reorderConfig.stepType,
      sourceTimePriorityResetUpdates: normalizeSegmentReorderTimePriorityResetUpdates(previewOverrides?.timePriorityResetUpdates),
      sourceTripId: typeof reorderConfig.tripId === "string" ? reorderConfig.tripId : "",
      sourceTruckLabel: row?.truckLabel || row?.assignment?.truck_name || row?.assignmentId || "",
      sourcePreviewLabel: previewLabel,
      sourcePreviewMeta: previewMeta,
    };

    setSegmentReorderInteractionState({
      phase: "dragging",
      sourceStepId: reorderConfig.stepId,
    });
    setSegmentReorderDragState(nextState);
    updateImmediateSegmentReorder(pointerPosition);
    return true;
  }, [assignmentInteractionsRef, closeSegmentActionPopover, toggleTruckFilterDropdown, updateImmediateSegmentReorder]);

  const commitSegmentReorder = React.useCallback((pointerPosition = null) => {
    const currentState = segmentReorderDragStateRef.current;
    if (!currentState?.sourceStepId) {
      return false;
    }

    const clientX = Number.isFinite(Number(pointerPosition?.clientX))
      ? Number(pointerPosition.clientX)
      : currentState.pointerClientX;
    const clientY = Number.isFinite(Number(pointerPosition?.clientY))
      ? Number(pointerPosition.clientY)
      : currentState.pointerClientY;
    const intent = buildSegmentReorderIntent(
      currentState,
      resolveSegmentReorderDropTargetFromClientPoint(clientX, clientY),
    );
    if (!intent) {
      return false;
    }

    assignmentInteractionsRef?.current?.onCommitSegmentReorder?.(intent);
    return true;
  }, [assignmentInteractionsRef]);

  const finishImmediateSegmentReorder = React.useCallback(({ shouldCommit = false, pointerPosition = null } = {}) => {
    const currentState = segmentReorderDragStateRef.current;
    if (!currentState) {
      return false;
    }

    const didCommit = shouldCommit ? commitSegmentReorder(pointerPosition) : false;
    resetSegmentReorderDragState();
    return didCommit;
  }, [commitSegmentReorder, resetSegmentReorderDragState]);

  const handleSegmentReorderPointerDown = React.useCallback((event, row, segment) => {
    const reorderConfig = segment?.segmentReorder || null;
    if (event.button !== 0 || !event.isPrimary || !reorderConfig?.stepId || typeof window === "undefined") {
      return;
    }

    clearSegmentReorderPointerGesture();
    suppressSegmentReorderClickRef.current = false;
    setSegmentReorderInteractionState({
      phase: "pressing",
      sourceStepId: reorderConfig.stepId,
    });

    const gesture = {
      cleanup: null,
      dragStarted: false,
      holdElapsed: false,
      holdTimerId: null,
      lastClientX: event.clientX,
      lastClientY: event.clientY,
      movedPastThreshold: false,
      pointerId: event.pointerId,
      row,
      segment,
      startX: event.clientX,
      startY: event.clientY,
    };

    const maybeStartDrag = () => {
      if (gesture.dragStarted || !gesture.holdElapsed || !gesture.movedPastThreshold) {
        return;
      }
      gesture.dragStarted = startImmediateSegmentReorder(
        gesture.row,
        gesture.segment,
        { clientX: gesture.lastClientX, clientY: gesture.lastClientY },
      );
    };

    const finishGesture = (shouldCommit, pointerEvent = null) => {
      if (pointerEvent && pointerEvent.pointerId !== gesture.pointerId) {
        return;
      }

      const pointerPosition = {
        clientX: Number.isFinite(Number(pointerEvent?.clientX)) ? Number(pointerEvent.clientX) : gesture.lastClientX,
        clientY: Number.isFinite(Number(pointerEvent?.clientY)) ? Number(pointerEvent.clientY) : gesture.lastClientY,
      };

      clearSegmentReorderPointerGesture();
      if (gesture.dragStarted) {
        finishImmediateSegmentReorder({ shouldCommit, pointerPosition });
        return;
      }
      setSegmentReorderInteractionState({ phase: "idle", sourceStepId: "" });
    };

    const handleWindowPointerMove = (moveEvent) => {
      if (moveEvent.pointerId !== gesture.pointerId) {
        return;
      }

      gesture.lastClientX = moveEvent.clientX;
      gesture.lastClientY = moveEvent.clientY;
      if (!gesture.movedPastThreshold) {
        const deltaX = moveEvent.clientX - gesture.startX;
        const deltaY = moveEvent.clientY - gesture.startY;
        gesture.movedPastThreshold = Math.hypot(deltaX, deltaY) >= SEGMENT_REORDER_POINTER_MOVE_THRESHOLD_PX;
      }

      maybeStartDrag();
      if (gesture.dragStarted) {
        updateImmediateSegmentReorder({ clientX: moveEvent.clientX, clientY: moveEvent.clientY });
      }
    };

    const handleWindowPointerUp = (upEvent) => {
      finishGesture(true, upEvent);
    };

    const handleWindowPointerCancel = (cancelEvent) => {
      finishGesture(false, cancelEvent);
    };

    const handleWindowBlur = () => {
      finishGesture(false);
    };

    const handleWindowKeyDown = (keyEvent) => {
      if (keyEvent.key !== "Escape") {
        return;
      }
      keyEvent.preventDefault();
      finishGesture(false);
    };

    gesture.cleanup = () => {
      window.removeEventListener("pointermove", handleWindowPointerMove, true);
      window.removeEventListener("pointerup", handleWindowPointerUp, true);
      window.removeEventListener("pointercancel", handleWindowPointerCancel, true);
      window.removeEventListener("blur", handleWindowBlur);
      window.removeEventListener("keydown", handleWindowKeyDown, true);
    };

    gesture.holdTimerId = window.setTimeout(() => {
      gesture.holdTimerId = null;
      gesture.holdElapsed = true;
      maybeStartDrag();
    }, SEGMENT_REORDER_POINTER_HOLD_DELAY_MS);

    window.addEventListener("pointermove", handleWindowPointerMove, true);
    window.addEventListener("pointerup", handleWindowPointerUp, true);
    window.addEventListener("pointercancel", handleWindowPointerCancel, true);
    window.addEventListener("blur", handleWindowBlur);
    window.addEventListener("keydown", handleWindowKeyDown, true);
    segmentReorderPointerGestureRef.current = gesture;
  }, [
    clearSegmentReorderPointerGesture,
    finishImmediateSegmentReorder,
    startImmediateSegmentReorder,
    updateImmediateSegmentReorder,
  ]);

  return {
    clearSegmentReorderClickSuppression,
    clearSegmentReorderPointerGesture,
    consumeSegmentReorderClickSuppression,
    finishImmediateSegmentReorder,
    handleSegmentReorderPointerDown,
    resetSegmentReorderDragState,
    segmentReorderDragState,
    segmentReorderInteractionState,
    startImmediateSegmentReorder,
    updateImmediateSegmentReorder,
  };
}
